import { Box, useTheme } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { RiskIndicator, Tooltip } from 'packages/eid-ui'
import ViolationDetails from './ViolationDetails'
import { useTranslation } from 'react-i18next'
import RiskReason from './RiskReason'
import { ICartItemRisk } from '../types'
import classNames from 'classnames'

export const smallScreenWidth = 959

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
        },
        riskDetails: {
            display: 'flex',
        },
        riskIndicator: {
            width: '90px',
        },
        details: {
            marginLeft: '16px',
            display: 'flex',
            flexDirection: 'column',
            [`@media (max-width:${smallScreenWidth}px)`]: {
                justifyContent: 'center',
            },
        },
        detailsHeading: {
            fontSize: '14px',
            fontWeight: 600,
            color: theme?.palette?.common?.black,
            lineHeight: 1.43,
        },
        detailsHeadingHover: {
            '&:hover': {
                cursor: 'pointer',
                textDecoration: 'underline',
            },
        },
        detailsLabel: {
            fontSize: '12px',
            color: '#5d6870',
            lineHeight: 1.33,
        },
        friendlyNameDivMobile: {
            fontSize: '14px',
            fontWeight: 'bold',
            lineHeight: 1.43,
            padding: '5px 0px',

            [`@media (min-width:${smallScreenWidth}px)`]: {
                display: 'none',
            },
        },
        friendlyNameDiv: {
            [`@media (max-width:${smallScreenWidth}px)`]: {
                display: 'none',
            },
        },
        textWrap: {
            wordBreak: 'break-word',
            wordWrap: 'break-word',
            overflow: 'auto',
        },
    }),
)

const tooltipStyles = {
    minWidth: '376px',
    boxShadow:
        '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)',
    borderRadius: '5px',
}

const CartItemViolation = ({
    risk,
    showReason = false,
}: {
    risk: ICartItemRisk
    showReason?: boolean
}) => {
    const { t } = useTranslation()

    const classes = useStyles()
    const theme = useTheme()

    const friendlyName = (
        <Box
            className={classNames(classes.detailsHeading, {
                [classes.detailsHeadingHover]: !showReason,
            })}
        >
            {risk.friendlyName}
        </Box>
    )

    const friendlyNameDiv = !showReason ? (
        <Tooltip
            title={<ViolationDetails risk={risk} />}
            enterDelay={800}
            color={theme?.palette?.common?.white}
            fontColor={theme?.palette?.common?.black}
            tooltipStyles={tooltipStyles}
            arrow={true}
            disableInteractive={false}
        >
            {friendlyName}
        </Tooltip>
    ) : (
        friendlyName
    )

    const bindRiskReasons = () => {
        if (risk?.groupedReason?.length) {
            return risk.groupedReason.map((item: any, index: number) => {
                return <RiskReason key={`risk-reason-${index}`} risk={item} />
            })
        } else {
            return <RiskReason risk={risk} />
        }
    }

    return (
        <Box className={classes.root}>
            <Box
                className={classNames(
                    classes.friendlyNameDivMobile,
                    classes.textWrap,
                )}
            >
                {friendlyNameDiv}
            </Box>

            <Box className={classes.riskDetails}>
                <Box className={classes.riskIndicator}>
                    <RiskIndicator
                        label={t(`Common_${risk.riskLevel}Risk`)}
                        variant={risk.riskLevel}
                    />
                </Box>
                <Box className={classes.details}>
                    <Box
                        className={classNames(
                            classes.friendlyNameDiv,
                            classes.textWrap,
                        )}
                    >
                        {friendlyNameDiv}
                    </Box>

                    <Box className={classes.detailsLabel}>
                        {t(`Common_${risk.riskType}`)}
                    </Box>
                </Box>
            </Box>

            {showReason && bindRiskReasons()}
        </Box>
    )
}

export default CartItemViolation
